import { State } from 'interfaces/state'
import moment from 'moment'
import { RouteComponentProps } from 'react-router-dom'
import { createSelector, Selector } from 'reselect'
import { CategorizedPageTyped, PageType, PageTyped, TimeframedPageTyped, CategorizedPage } from 'interfaces/pages'
import { FeatureFlag, TimeframeTyped } from 'interfaces/global'
import { Category, MetaDataState, MoneyflowCategoryGroup, TypedMoneyflowCategory } from 'interfaces/meta'
import { URL_PAGE_MAP, SPENDING_PAGE, TRANSACTIONS_PAGE, INCOME_PAGE, DEBTS_PAGE, ASSETS_PAGE } from 'constants/pages'
import { isCategorizedPage, isInternalUrl, isShrunkSidebarUrl } from 'helpers/pageHelpers'
import { getTimeframeLastMonth, createGetAgeMonthPrecision } from 'helpers/dateTimeHelpers'
import { UnexpectedErrorType } from 'interfaces/errors'
import { currentViewportSelector } from 'selectors/viewportSelectors'
import { compareViewports } from 'helpers/viewportHelpers'
import { CardBlockOwnProps } from 'components/features/pfm/Blocks/Blocks'
import { getItemsFromCollection } from 'helpers/normalizeHelpers'
import { ALL_CATEGORY_ID, TRANSFER_CATEGORY_ID } from 'constants/meta'
import { MoneyflowType } from 'interfaces/moneyflow'
import { ViewportMatchName } from 'interfaces/viewport'
import { PeergroupPopupDetails } from 'interfaces/peergroups'
import { SidebarPane } from 'interfaces/sidebar'
import { makeUrlRelative } from 'helpers/utilsHelpers'
import { createShallowEqualSelector } from 'helpers/selectorHelpers'

export const isNotNullSelector = (entity: any | null) => entity !== null

export const createOwnPropsSelector = <TOwnProps>() => createShallowEqualSelector(
  (_state: State, ownProps: TOwnProps): TOwnProps => ownProps,
  (ownProps) => ownProps
)

export const featureFlagsSelector = (state: State) =>
  state.data.flags

export const featureFlagSelector = createSelector(
  featureFlagsSelector,
  createOwnPropsSelector<FeatureFlag>(),
  (flags, flag) => {
    return flags?.includes(flag)
  }
)

export const memberSelector = (state: State) =>
  state.data.member


export const memberDataSelector = createSelector(
  memberSelector,
  (member) => member ? member.data : null
)

export const memberBirthDateSelector = createSelector(
  memberDataSelector,
  (data) => {
    if (!data || !data.info || !data.isAuthenticated || !data.info.birth_date) {
      return null
    }
    return data.info.birth_date
  }
)

export const memberAgeSelector = createSelector(
  memberBirthDateSelector,
  (birthDate) => {
    if (!birthDate) {
      return null
    }
    return moment().diff(birthDate, 'years')
  }
)

export const getAgeSelector = createSelector(
  memberBirthDateSelector,
  (birthDate) => {

    if (!birthDate) {
      return null
    }
    return createGetAgeMonthPrecision(birthDate)
  }
)

export const isMemberDataPresentSelector = createSelector(
  memberDataSelector,
  Boolean
)

export const memberInfoSelector = createSelector(
  memberDataSelector,
  (data) => data?.info ?? null
)

export const isAuthenticatedSelector = (state: State) =>
  state.data.member.data?.isAuthenticated ?? null

export const hasLinkedAccountsSelector = (state: State) =>
  state.data.member.data?.info?.has_linked_accounts ?? false

export const hasFirstLinkedAccountSelector = (state: State) =>
  state.data.member.data?.info?.already_linked_first_account ?? false

export const isMemberElligibleSelector = (state: State) =>
  state.data.member.data?.info?.is_meeting_eligible ?? false

export const hadAppointedMeetingSelector = (state: State) =>
  state.data.member.data?.info?.had_appointed_meeting ?? false

export const isMemberPremiumSelector = (state: State) =>
  state.data.member.data?.info?.subscription.type === 'premium'

export const isMemberPowerUserSelector = createSelector(
  isMemberPremiumSelector,
  hasLinkedAccountsSelector,
  memberInfoSelector,
  (isPremium, hasLinkedAccounts, memberInfo) => !!isPremium && !!hasLinkedAccounts && memberInfo?.is_creditfile_linked
)

type OwnProps = RouteComponentProps<{}>
export const errorTypeSelector = (state: State, ownProps: OwnProps): UnexpectedErrorType | null => {
  const { error } = state.app
  if (error) {
    return error.errorType
  }

  const member = memberInfoSelector(state)
  if (member && member.is_blocked) {
    const url = currentUrlSelector(state, ownProps)
    if (isInternalUrl(url)) {
      return 'user-blocked'
    }
  }

  return null
}

export const metaSelector: Selector<State, MetaDataState> = (state: State) =>
  state.data.meta

export const metaCategoriesSelector = createSelector(
  metaSelector,
  createOwnPropsSelector<CategorizedPageTyped>(),
  (meta, { type }) => {
    if (type === 'spending') {
      return meta.spending_categories
    }

    if (type === 'income') {
      return meta.income_categories
    }

    if (type === 'debts') {
      return meta.debt_categories
    }

    if (type === 'assets') {
      return meta.asset_categories
    }

    // this will never be returned but TS can't understand that all the types are listed here
    return meta.spending_categories
  }
)

const saturateCategoryWithType = (type: MoneyflowType | null) => (category: Category): TypedMoneyflowCategory => ({
  ...category,
  type,
})

const OMITTED_CATEGORY_IDS = new Set([TRANSFER_CATEGORY_ID, ALL_CATEGORY_ID])

const saturateCategory = (type: MoneyflowType | null) => (category: Category): TypedMoneyflowCategory => ({
  ...category,
  type,
})

export const categoryGroupsSelector: Selector<State, MoneyflowCategoryGroup[]> = createSelector(
  metaSelector,
  (meta) => {
    const spendingCategories = getItemsFromCollection<Category, 'id'>(meta.spending_categories)

    return [
      {
        id: 'spending',
        name: 'Spending',
        categories: spendingCategories
          .filter(({ id }) => !OMITTED_CATEGORY_IDS.has(id))
          .map(saturateCategory('spending')),
      },
      {
        id: 'income',
        name: 'Income',
        categories: getItemsFromCollection<Category, 'id'>(meta.income_categories)
          .filter(({ id }) => !OMITTED_CATEGORY_IDS.has(id))
          .map(saturateCategory('income')),
      },
      {
        id: 'transfers',
        name: 'Transfers',
        categories: spendingCategories
          .filter(({ id }) => id === TRANSFER_CATEGORY_ID)
          .map(saturateCategory(null)),
      },
    ]
  }
)

export const createSelectedCategoryIdSelector = () =>
  <TCategorizedPage extends CategorizedPage>(state: State, type: TCategorizedPage): State['app'][TCategorizedPage]['categories']['selectedId'] =>
    state.app[type].categories.selectedId

export const categoriesGroupsSelector = createSelector(
  metaSelector,
  (_state: State, ownProps: CategorizedPageTyped) => ownProps,
  (meta, { type }) => {
    const groups: MoneyflowCategoryGroup[] = []

    if (type === SPENDING_PAGE || type === TRANSACTIONS_PAGE) {
      groups.push({
        id: 'spending',
        name: 'Spending',
        categories: getItemsFromCollection<Category, 'id'>(meta.spending_categories)
          .filter(({ id }) => !OMITTED_CATEGORY_IDS.has(id))
          .map(saturateCategoryWithType('spending')),
      })
    }

    if (type === INCOME_PAGE || type === TRANSACTIONS_PAGE) {
      groups.push({
        id: 'income',
        name: 'Income',
        categories: getItemsFromCollection<Category, 'id'>(meta.income_categories)
          .filter(({ id }) => !OMITTED_CATEGORY_IDS.has(id))
          .map(saturateCategoryWithType('income')),
      })
    }

    if (type === TRANSACTIONS_PAGE) {
      groups.push({
        id: 'transfers',
        name: 'Transfers',
        categories: getItemsFromCollection<Category, 'id'>(meta.spending_categories)
          .filter(({ id }) => id === TRANSFER_CATEGORY_ID)
          .map(saturateCategoryWithType(null)),
      })
    }

    if (type === DEBTS_PAGE) {
      groups.push({
        id: 'debts',
        name: 'Debts',
        categories: getItemsFromCollection<Category, 'id'>(meta.debt_categories)
          .filter(({ id }) => !OMITTED_CATEGORY_IDS.has(id))
          .map(saturateCategoryWithType(null)),
      })
    }

    if (type === ASSETS_PAGE) {
      groups.push({
        id: 'assets',
        name: 'Assets',
        categories: getItemsFromCollection<Category, 'id'>(meta.asset_categories)
          .filter(({ id }) => !OMITTED_CATEGORY_IDS.has(id))
          .map(saturateCategoryWithType(null)),
      })
    }

    return groups
  }
)

export const currentUrlSelector = (_state: State, ownProps: { location: Pick<Location, 'pathname' | 'search' | 'hash'> }): string =>
  makeUrlRelative(ownProps.location)

export const isShrunkSidebarSelector = createSelector(
  currentUrlSelector,
  isAuthenticatedSelector,
  (currentUrl, isAuthenticated) => !!currentUrl && isShrunkSidebarUrl(currentUrl, Boolean(isAuthenticated))
)

export const currentPageSelector = createSelector(
  currentUrlSelector,
  (url): PageType => URL_PAGE_MAP[url]
)

export const lastMonthOfTimeframeSelector = createSelector(
  createOwnPropsSelector<TimeframeTyped>(),
  ({ timeframe }) => getTimeframeLastMonth(timeframe),
)

export const timeframeSelector = (state: State, { type }: TimeframedPageTyped) =>
  state.app[type].timeframe

export const categorySelector = (state: State, { type }: CategorizedPageTyped) =>
  state.app[type].categories.selectedId

export const optionalSelectedCategorySelector = (state: State, { type }: PageTyped) => {
  if (!isCategorizedPage(type)) {
    return null
  }

  return categorySelector(state, { type })
}

export const blockCornersSelector = createSelector(
  currentViewportSelector,
  createOwnPropsSelector<CardBlockOwnProps>(),
  (currentViewport, ownProps) =>
    ownProps.corners
      ? ownProps.corners
      : (compareViewports(currentViewport, '<', 'w375') ? 'none' : 'all')
)

export const createBalloonArrowDirectionSelector = (leftDirectionMinViewport: ViewportMatchName) => createSelector(
  currentViewportSelector,
  (currentViewport) =>
    compareViewports(currentViewport, '<', leftDirectionMinViewport)
      ? 'down'
      : 'left'
)

export const shownSidebarPaneSelector = (state: State): SidebarPane | null =>
  state.app.sidebar.shownPane

export const peergroupPopupSelector = (state: State): PeergroupPopupDetails =>
  state.app.sidebar.peergroupPopup

export const categoriesAppSelector = (state: State, { type }: CategorizedPageTyped) =>
  state.app[type].categories

export const isAllCategorySelectedSelector = createSelector(
  categoriesAppSelector,
  ({ selectedId }) => selectedId === ALL_CATEGORY_ID
)

export const dateNowSelector = () => Date.now()
